/**
 * TimelineItemDrawer — leesbare rechter-drawer voor één klant-tijdlijn-item.
 *
 * Veralgemenisering van de oude EmailPreviewDrawer: rendert de volledige inhoud
 * per `contentKind`:
 *   - sf_email   → Salesforce EmailMessage (lazy HTML via /api/customers/email-body, sandboxed iframe)
 *   - text       → proactieve notificatie (SMS-tekst / e-mail-text, inline body)
 *   - message    → inbox-bericht (body_text inline) + "Open in inbox"
 *   - marketing  → marketing-mail: volledige body zoals verstuurd via
 *                  /api/marketing/send-body, sandboxed iframe. Engagement
 *                  (opens/clicks) blijft in de Marketing-tab.
 *
 * Telefonie-items renderen NIET hier: die openen het bestaande gespreksdetail
 * (CallDetailDrawerPanel via useCallDrawerStore) — zie KlantDetailView.
 *
 * Plan: plans/2026-06-27-uniforme-klant-communicatietijdlijn.md (Stap 5).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { formatDateTime, formatEmailStatus } from '../../lib/format';
import { fetchMarketingSendBody, type MarketingSendBody } from '../../lib/marketing-send-body';

export interface TimelineDrawerTarget {
  // 'call' wordt nooit door deze drawer gerenderd (telefonie opent het
  // bestaande gespreksdetail), maar zit in de union zodat een volledig
  // TimelineItem structureel toewijsbaar is.
  contentKind: 'sf_email' | 'text' | 'message' | 'marketing' | 'call';
  contentRef?: string | null;
  subject?: string;
  snippet?: string;
  body?: string | null;
  channel?: string;
  status?: string;
  date?: string;
  brand?: string;
  fromName?: string;
  isIncoming?: boolean;
  conversationId?: string | null;
}

interface SfEmailBody {
  id: string;
  subject: string;
  messageDate: string;
  fromAddress: string;
  fromName: string;
  toAddress: string;
  ccAddress: string | null;
  bccAddress: string | null;
  htmlBody: string;
  textBody: string;
  hasAttachment: boolean;
  status: number;
  isIncoming: boolean;
}

interface TimelineItemDrawerProps {
  item: TimelineDrawerTarget | null;
  onClose: () => void;
}

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  sent: { label: 'Verzonden', color: 'bg-emerald-100 text-emerald-700' },
  delivered: { label: 'Afgeleverd', color: 'bg-emerald-100 text-emerald-700' },
  suppressed: { label: 'Niet verzonden', color: 'bg-amber-100 text-amber-700' },
  failed: { label: 'Mislukt', color: 'bg-rose-100 text-rose-700' },
  bounced: { label: 'Gebounced', color: 'bg-rose-100 text-rose-700' },
  queued: { label: 'In wachtrij', color: 'bg-slate-100 text-slate-600' },
};

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-700 break-words">{value}</dd>
    </>
  );
}

export function TimelineItemDrawer({ item, onClose }: TimelineItemDrawerProps) {
  const navigate = useNavigate();
  const [emailBody, setEmailBody] = useState<SfEmailBody | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mktBody, setMktBody] = useState<MarketingSendBody | null>(null);
  const [mktLoading, setMktLoading] = useState(false);
  const [mktError, setMktError] = useState<string | null>(null);

  const isSfEmail = item?.contentKind === 'sf_email';
  const emailId = isSfEmail ? item?.contentRef ?? null : null;
  const marketingSendId = item?.contentKind === 'marketing' ? item?.contentRef ?? null : null;

  // Lazy marketing-mail laden — de volledige body zoals verstuurd.
  useEffect(() => {
    if (!marketingSendId) {
      setMktBody(null);
      setMktError(null);
      return;
    }
    let cancelled = false;
    setMktLoading(true);
    setMktError(null);
    setMktBody(null);
    fetchMarketingSendBody(marketingSendId)
      .then((data) => {
        if (!cancelled) setMktBody(data);
      })
      .catch((err) => {
        if (!cancelled) setMktError(err instanceof Error ? err.message : 'Ophalen mislukt');
      })
      .finally(() => {
        if (!cancelled) setMktLoading(false);
      });
    return () => { cancelled = true; };
  }, [marketingSendId]);

  // Lazy SF-mail-body laden (alleen voor sf_email-items).
  useEffect(() => {
    if (!emailId) {
      setEmailBody(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setEmailBody(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          if (!cancelled) setError('Niet ingelogd');
          return;
        }
        const res = await fetch(`/api/customers/email-body?id=${encodeURIComponent(emailId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Ophalen mislukt' }));
          if (!cancelled) setError(err.error || 'Ophalen mislukt');
          return;
        }
        const data: SfEmailBody = await res.json();
        if (!cancelled) setEmailBody(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Netwerkfout');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [emailId]);

  // Esc sluit.
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  const headerTitle = emailBody?.subject || item.subject || (loading ? 'Laden…' : 'Bericht');
  const statusBadge = isSfEmail
    ? (emailBody ? formatEmailStatus(emailBody.status) : null)
    : (item.status ? STATUS_BADGE[item.status] ?? { label: item.status, color: 'bg-slate-100 text-slate-600' } : null);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/30 z-40" onClick={onClose} aria-hidden />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[680px] bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <button onClick={onClose} aria-label="Sluiten" className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-slate-900 truncate flex-1">{headerTitle}</h2>
          {statusBadge && (
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', statusBadge.color)}>
              {statusBadge.label}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* ── SF-mail ─────────────────────────────────────── */}
          {isSfEmail && (
            <>
              {loading && (
                <div className="p-6 space-y-3 animate-pulse">
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-3 bg-slate-100 rounded w-3/4" />
                  <div className="h-64 bg-slate-100 rounded mt-4" />
                </div>
              )}
              {error && (
                <div className="m-5 px-4 py-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
                  {error}
                </div>
              )}
              {emailBody && !loading && !error && (
                <>
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                    <dl className="grid grid-cols-[80px_1fr] gap-y-1 text-xs">
                      <MetaRow label="Van" value={emailBody.fromName ? `${emailBody.fromName} <${emailBody.fromAddress}>` : emailBody.fromAddress} />
                      <MetaRow label="Naar" value={emailBody.toAddress} />
                      {emailBody.ccAddress && <MetaRow label="Cc" value={emailBody.ccAddress} />}
                      {emailBody.bccAddress && <MetaRow label="Bcc" value={emailBody.bccAddress} />}
                      <MetaRow label="Datum" value={formatDateTime(emailBody.messageDate)} />
                      {emailBody.hasAttachment && <MetaRow label="Bijlagen" value="Ja" />}
                    </dl>
                  </div>
                  <div className="p-5">
                    {emailBody.htmlBody ? (
                      <iframe
                        title="E-mail body"
                        srcDoc={emailBody.htmlBody}
                        sandbox=""
                        className="w-full h-[calc(100vh-260px)] border border-slate-200 rounded bg-white"
                      />
                    ) : emailBody.textBody ? (
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{emailBody.textBody}</pre>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Geen body beschikbaar.</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Notificatie (SMS / e-mail-text) ─────────────── */}
          {item.contentKind === 'text' && (
            <>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                <dl className="grid grid-cols-[80px_1fr] gap-y-1 text-xs">
                  <MetaRow label="Kanaal" value={item.channel === 'sms' ? 'SMS' : 'E-mail'} />
                  <MetaRow label="Van" value={item.fromName || 'Kleda (automatisch)'} />
                  {item.date && <MetaRow label="Datum" value={formatDateTime(item.date)} />}
                </dl>
              </div>
              <div className="p-5">
                {item.body ? (
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{item.body}</pre>
                ) : (
                  <p className="text-sm text-slate-400 italic">Geen inhoud bewaard (oudere notificatie).</p>
                )}
              </div>
            </>
          )}

          {/* ── Inbox-bericht ───────────────────────────────── */}
          {item.contentKind === 'message' && (
            <>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                <dl className="grid grid-cols-[80px_1fr] gap-y-1 text-xs">
                  <MetaRow label={item.isIncoming ? 'Van' : 'Naar'} value={item.fromName || '(onbekend)'} />
                  <MetaRow label="Kanaal" value={item.channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'} />
                  {item.date && <MetaRow label="Datum" value={formatDateTime(item.date)} />}
                </dl>
              </div>
              <div className="p-5 space-y-4">
                {item.body ? (
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{item.body}</pre>
                ) : (
                  <p className="text-sm text-slate-400 italic">Geen inhoud beschikbaar.</p>
                )}
                {item.conversationId && (
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(`/inbox/${item.conversationId}`); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                    Open in inbox
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Marketing-mail ──────────────────────────────── */}
          {item.contentKind === 'marketing' && (
            <>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                <dl className="grid grid-cols-[90px_1fr] gap-y-1 text-xs">
                  <MetaRow label="Onderwerp" value={mktBody?.subject || item.subject || '(geen onderwerp)'} />
                  {(mktBody?.via || item.snippet) && (
                    <MetaRow label="Campagne" value={mktBody?.via || item.snippet || ''} />
                  )}
                  {mktBody?.toEmail && <MetaRow label="Naar" value={mktBody.toEmail} />}
                  {(mktBody?.sentAt || item.date) && (
                    <MetaRow label="Datum" value={formatDateTime(mktBody?.sentAt || item.date)} />
                  )}
                  {mktBody?.discountOffer && (
                    <MetaRow
                      label="Aanbod"
                      value={`${mktBody.discountOffer.percentage}%${mktBody.discountOffer.code ? ` · code ${mktBody.discountOffer.code}` : ''}`}
                    />
                  )}
                </dl>
              </div>

              {mktLoading && (
                <div className="p-6 space-y-3 animate-pulse">
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-64 bg-slate-100 rounded mt-4" />
                </div>
              )}

              {mktError && !mktLoading && (
                <div className="m-5 px-4 py-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
                  {mktError}
                </div>
              )}

              {mktBody && !mktLoading && !mktError && (
                <div className="p-5 space-y-3">
                  {/* Hardheid van de inhoud expliciet tonen: een reconstructie is
                      geen bewijs en mag er ook niet als bewijs uitzien. */}
                  {mktBody.source === 'reconstructie' && (
                    <div className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                      <strong>Reconstructie, geen kopie.</strong> {mktBody.warning}
                    </div>
                  )}
                  {mktBody.source === 'onbeschikbaar' && (
                    <div className="px-3 py-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
                      {mktBody.warning || 'De inhoud van deze mail is niet beschikbaar.'}
                    </div>
                  )}
                  {mktBody.source === 'opgeslagen' && (
                    <div className="px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800">
                      Exact zoals verstuurd, opgeslagen op het verzendmoment.
                    </div>
                  )}

                  {mktBody.html ? (
                    <iframe
                      title="Marketing-mail"
                      srcDoc={mktBody.html}
                      sandbox=""
                      className="w-full h-[calc(100vh-340px)] border border-slate-200 rounded bg-white"
                    />
                  ) : mktBody.text ? (
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{mktBody.text}</pre>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Geen body beschikbaar.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
